/**
 * Best-effort mapping from Cost Explorer "Service" names → cost engine line item ids.
 * Each CSV row is assigned to at most one line; remainder counts as unmapped.
 */
export type ExplorerMappedToLines = {
  lineUsd: Record<string, number>;
  /** Sum of services that did not match any known pattern. */
  unmappedUsd: number;
};

function classifyServiceName(name: string): string | null {
  const n = name.toLowerCase().trim();
  if (!n) return null;

  if (/\bopenai\b|\banthropic\b|\bbedrock\b|\bsagemaker\b/i.test(n)) return "llm";
  if (/lambda/.test(n)) return "lambda";
  if (/api gateway|apigateway/.test(n)) return "apigw";
  if (/dynamodb|dynamo db/.test(n)) return "dynamo";
  if (/\bs3\b|simple storage service|amazon s3/.test(n)) return "s3";
  if (/cloudwatch|cloud watch/.test(n)) return "cw";
  if (/cloudfront/.test(n)) return "cloudfront";
  if (/route\s*53/.test(n)) return "route53";
  if (/\bwaf\b|web application firewall|shield advanced/.test(n)) return "alb_waf";
  if (/guardduty|guard duty/.test(n)) return "guardduty";
  if (/eks|elastic kubernetes/.test(n)) return "eks";
  if (/elasticache|memorydb/.test(n)) return "ec2_app";
  if (/relational database|rds\b|aurora/.test(n)) return "rds";
  if (/ec2[- ]|elastic compute|nat gateway|elastic ip|elb|load balancer|application load|network load|lightsail/.test(n))
    return "ec2_app";
  if (/vpc|virtual private cloud|direct connect|transit gateway|private link|endpoint/.test(n))
    return "vpc_private";
  if (/data transfer|bandwidth|egress|traffic mirror/.test(n)) return "xfer";

  return null;
}

export function mapExplorerServicesToLineUsd(
  byService: Record<string, number>,
): ExplorerMappedToLines {
  const lineUsd: Record<string, number> = {};
  let mappedSum = 0;
  let total = 0;

  for (const [name, raw] of Object.entries(byService)) {
    const amt = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
    total += amt;
    const id = classifyServiceName(name);
    if (id) {
      lineUsd[id] = (lineUsd[id] ?? 0) + amt;
      mappedSum += amt;
    }
  }

  const unmappedUsd = Math.max(0, Math.round((total - mappedSum) * 100) / 100);
  for (const k of Object.keys(lineUsd)) {
    lineUsd[k] = Math.round((lineUsd[k] ?? 0) * 100) / 100;
  }
  return { lineUsd, unmappedUsd };
}
